/**
 * Volumetrische lichtschachten — zonlicht door de daklichten, daglicht door de
 * dockdeuren — als nabewerking met de dieptebuffer en de schaduwkaart.
 *
 * ## Waarom als nabewerking en niet als mesh
 *
 * De eerste gedachte is een doorschijnende doos onder elk daklicht. Die heeft
 * drie problemen die elk op zich het beeld verraden:
 *
 *  1. Waar de doos een stelling of de vloer snijdt, staat een harde lijn.
 *  2. Loopt er een stelling dóór de schacht, dan gloeit het licht achter de
 *     stelling gewoon mee, want een mesh weet niet wat erachter staat.
 *  3. Er zit geen schaduw in. Een echte lichtschacht in een magazijn is geen
 *     egale balk maar een bundel strepen: de gordingen, de spanten en de
 *     stellingen snijden er donkere banen in. Dat is wat hem "echt" maakt.
 *
 * Hier wordt per pixel de kijkstraal door elke schacht gevolgd, tot aan het
 * eerste oppervlak dat de dieptebuffer kent (lost 1 en 2 op), en op elk
 * monsterpunt gevraagd of de zon daar komt, via de schaduwkaart van hetzelfde
 * zonlicht dat de vloervlekken maakt (lost 3 op). Schacht en vlek komen dus
 * uit één bron en kunnen niet uit elkaar lopen.
 *
 * ## Kosten
 *
 * Per pixel een doos-straaltest per schacht (goedkoop), en alleen waar de
 * straal een schacht raakt `MONSTERS` monsters met een schaduwopzoeking. Het
 * monsterpatroon verspringt per pixel en per frame; de filmkorrel verderop in
 * de keten maakt daar ruis van in plaats van ringen.
 */
import { Effect, EffectAttribute, BlendFunction } from 'postprocessing'
import * as THREE from 'three'

export const MAX_SCHACHTEN = 12
const MONSTERS = 10

const fragment = /* glsl */ `
#define MAX_SCHACHTEN ${MAX_SCHACHTEN}
#define MONSTERS ${MONSTERS}

uniform mat4 inverseProjection;
uniform mat4 cameraWereld;
uniform vec3 cameraPos;
uniform mat4 schachtInv[MAX_SCHACHTEN];
uniform vec4 schachtKleur[MAX_SCHACHTEN];   // rgb = licht, a = 1 als de schaduwkaart meedoet
uniform vec3 schachtMaat[MAX_SCHACHTEN];    // lengte, breedte, diepte in meters
uniform int aantal;
uniform sampler2D schaduwKaart;
uniform mat4 schaduwMatrix;
uniform float schaduwAan;
uniform vec3 zonRichting;
uniform float sterkte;

float hash13(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.zyx + 31.32);
  return fract((p.x + p.y) * p.z);
}

float waardeRuis(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash13(i);
  float b = hash13(i + vec3(1, 0, 0));
  float c = hash13(i + vec3(0, 1, 0));
  float d = hash13(i + vec3(1, 1, 0));
  float e = hash13(i + vec3(0, 0, 1));
  float g = hash13(i + vec3(1, 0, 1));
  float h = hash13(i + vec3(0, 1, 1));
  float k = hash13(i + vec3(1, 1, 1));
  return mix(mix(mix(a, b, f.x), mix(c, d, f.x), f.y), mix(mix(e, g, f.x), mix(h, k, f.x), f.y), f.z);
}

float zonZichtbaar(vec3 p) {
  if (schaduwAan < 0.5) return 1.0;
  vec4 s = schaduwMatrix * vec4(p, 1.0);
  s.xyz /= s.w;
  if (s.x < 0.0 || s.x > 1.0 || s.y < 0.0 || s.y > 1.0 || s.z > 1.0) return 1.0;
  float diepte = unpackRGBAToDepth(texture2D(schaduwKaart, s.xy));
  return step(s.z - 0.0015, diepte);
}

// Henyey-Greenstein: stof verstrooit vooruit. g = 0,35 is lichte nevel.
float fase(float cosT) {
  const float g = 0.35;
  float g2 = g * g;
  return (1.0 - g2) / pow(1.0 + g2 - 2.0 * g * cosT, 1.5) * 0.35;
}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  if (aantal == 0 || sterkte <= 0.0) { outputColor = inputColor; return; }

  // Kijkstraal in wereldruimte.
  vec4 ver = inverseProjection * vec4(uv * 2.0 - 1.0, 1.0, 1.0);
  vec3 richtingView = normalize(ver.xyz / ver.w);
  vec3 rd = normalize(mat3(cameraWereld) * richtingView);
  vec3 ro = cameraPos;

  // Afstand tot het eerste oppervlak, uit de dieptebuffer.
  float diepte = readDepth(uv);
  float viewZ = getViewZ(diepte);
  float maxT = diepte >= 1.0 ? 1e4 : viewZ / richtingView.z;

  float zicht = fase(dot(rd, zonRichting));
  float jitter = hash13(vec3(uv * resolution, time * 60.0));

  vec3 licht = vec3(0.0);

  for (int i = 0; i < MAX_SCHACHTEN; i++) {
    if (i >= aantal) break;

    // Naar de eenheidskubus van deze schacht. De richting blijft onge-
    // normaliseerd, zodat t in beide ruimtes dezelfde wereldafstand is.
    vec3 o = (schachtInv[i] * vec4(ro, 1.0)).xyz;
    vec3 d = mat3(schachtInv[i]) * rd;
    vec3 inv = 1.0 / d;
    vec3 t0 = (vec3(0.0) - o) * inv;
    vec3 t1 = (vec3(1.0) - o) * inv;
    vec3 tMin = min(t0, t1);
    vec3 tMax = max(t0, t1);
    float tIn = max(max(max(tMin.x, tMin.y), tMin.z), 0.0);
    float tUit = min(min(min(tMax.x, tMax.y), tMax.z), maxT);
    if (tUit <= tIn) continue;

    vec3 maat = schachtMaat[i];
    float stap = (tUit - tIn) / float(MONSTERS);
    float som = 0.0;
    for (int m = 0; m < MONSTERS; m++) {
      float t = tIn + (float(m) + jitter) * stap;
      vec3 q = o + d * t;           // 0..1 in de schacht
      vec3 w = ro + rd * t;         // wereld

      // Zachte randen dwars (y), zachte uiteinden in de lengte (x), en een
      // schacht die van boven (z = 0) naar de vloer (z = 1) uitdooft.
      float dwars = smoothstep(0.0, 0.32, q.y) * smoothstep(1.0, 0.68, q.y);
      float lang = smoothstep(0.0, 0.04, q.x) * smoothstep(1.0, 0.96, q.x);
      float neer = smoothstep(0.0, 0.03, q.z) * mix(1.0, 0.4, q.z);

      // Strepen in de lengte van de schacht: stof dat in de stroming meedrijft.
      vec3 r = vec3(q.x * maat.x * 0.9, q.y * maat.y * 1.3, q.z * maat.z * 0.12);
      // Gekwadrateerd: de meeste stof is ijl, hier en daar een dichte sliert.
      // Lineair werd het een egale mist in plaats van een bundel strepen.
      float sliert = waardeRuis(r + vec3(0.0, 0.0, -time * 0.05));
      float wolk = waardeRuis(w * 0.35 + vec3(time * 0.03, 0.0, 0.0));
      float stof = 0.15 + 2.2 * sliert * sliert * (0.5 + 0.5 * wolk);

      float zon = schachtKleur[i].a > 0.5 ? zonZichtbaar(w) : 1.0;
      som += dwars * lang * neer * stof * zon;
    }
    float f = schachtKleur[i].a > 0.5 ? zicht : 0.7;
    licht += schachtKleur[i].rgb * som * stap * f;
  }

  outputColor = vec4(inputColor.rgb + licht * sterkte, inputColor.a);
}
`

export interface Schacht {
  /** Wereld → eenheidskubus. */
  inverse: THREE.Matrix4
  /** Licht per meter schacht, lineair. */
  kleur: THREE.Color
  /** Of de schaduwkaart van de zon meedoet. */
  zon: boolean
  maat: THREE.Vector3
}

export class LichtschachtenEffect extends Effect {
  camera: THREE.Camera
  zonLicht: THREE.DirectionalLight | null = null
  private schachten: Schacht[] = []

  constructor(camera: THREE.Camera) {
    super('LichtschachtenEffect', fragment, {
      // DEPTH voor de occlusie. CONVOLUTION is hier geen convolutie maar een
      // manier om een eigen pass af te dwingen: in dezelfde pass als bloom en
      // scherptediepte zouden die twee de schachten niet zien, want ze lezen
      // hun invoer vóór de effecten in hun pass.
      attributes: EffectAttribute.DEPTH | EffectAttribute.CONVOLUTION,
      blendFunction: BlendFunction.SET,
      uniforms: new Map<string, THREE.Uniform>([
        ['inverseProjection', new THREE.Uniform(new THREE.Matrix4())],
        ['cameraWereld', new THREE.Uniform(new THREE.Matrix4())],
        ['cameraPos', new THREE.Uniform(new THREE.Vector3())],
        ['schachtInv', new THREE.Uniform(Array.from({ length: MAX_SCHACHTEN }, () => new THREE.Matrix4()))],
        ['schachtKleur', new THREE.Uniform(Array.from({ length: MAX_SCHACHTEN }, () => new THREE.Vector4()))],
        ['schachtMaat', new THREE.Uniform(Array.from({ length: MAX_SCHACHTEN }, () => new THREE.Vector3(1, 1, 1)))],
        ['aantal', new THREE.Uniform(0)],
        ['schaduwKaart', new THREE.Uniform(null)],
        ['schaduwMatrix', new THREE.Uniform(new THREE.Matrix4())],
        ['schaduwAan', new THREE.Uniform(0)],
        ['zonRichting', new THREE.Uniform(new THREE.Vector3(0, 1, 0))],
        ['sterkte', new THREE.Uniform(1)],
      ]),
    })
    this.camera = camera
  }

  zetSchachten(schachten: Schacht[]) {
    this.schachten = schachten.slice(0, MAX_SCHACHTEN)
    const inv = this.uniforms.get('schachtInv')!.value as THREE.Matrix4[]
    const kl = this.uniforms.get('schachtKleur')!.value as THREE.Vector4[]
    const mt = this.uniforms.get('schachtMaat')!.value as THREE.Vector3[]
    this.schachten.forEach((s, i) => {
      inv[i].copy(s.inverse)
      kl[i].set(s.kleur.r, s.kleur.g, s.kleur.b, s.zon ? 1 : 0)
      mt[i].copy(s.maat)
    })
    this.uniforms.get('aantal')!.value = this.schachten.length
  }

  /** Alleen de kleur — voor de dockdeuren, die elk frame open- en dichtgaan. */
  zetKleur(i: number, kleur: THREE.Color) {
    const kl = this.uniforms.get('schachtKleur')!.value as THREE.Vector4[]
    if (kl[i]) kl[i].set(kleur.r, kleur.g, kleur.b, kl[i].w)
  }

  zetSterkte(s: number) {
    this.uniforms.get('sterkte')!.value = s
  }

  update() {
    const cam = this.camera as THREE.PerspectiveCamera
    this.uniforms.get('inverseProjection')!.value.copy(cam.projectionMatrixInverse)
    this.uniforms.get('cameraWereld')!.value.copy(cam.matrixWorld)
    this.uniforms.get('cameraPos')!.value.setFromMatrixPosition(cam.matrixWorld)

    const zon = this.zonLicht
    const kaart = zon?.castShadow ? zon.shadow.map?.texture ?? null : null
    this.uniforms.get('schaduwKaart')!.value = kaart
    this.uniforms.get('schaduwAan')!.value = kaart ? 1 : 0
    if (zon) {
      if (kaart) this.uniforms.get('schaduwMatrix')!.value.copy(zon.shadow.matrix)
      const r = this.uniforms.get('zonRichting')!.value as THREE.Vector3
      r.copy(zon.position).sub(zon.target.position).normalize()
    }
  }
}
